/**
 * Default path transition + routing callbacks (flow-level).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type {
  VoluumFlowUpdate,
  VoluumOfferRedirectMode,
  VoluumRealtimeRoutingApi,
} from '@trafficpulse/shared';

import PillSegmentRow from '../../../paths/settings/PillSegmentRow';
import PathTransitionFlowStrip from '../../../paths/settings/path-transition';
import { PATH_TRANSITION_OPTIONS } from '../../../paths/settings/path-settings.constants';
import FlowMetaFieldLabel from '../FlowMetaFieldLabel';
import { ROUTING_CALLBACK_PILL_OPTIONS } from '../flow-meta.constants';

export interface FlowRoutingFieldsProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
}

const FlowRoutingFields: FC<FlowRoutingFieldsProps> = ({ draft, onPatch }) => {
  const redirectMode: VoluumOfferRedirectMode =
    draft.defaultOfferRedirectMode ?? 'REGULAR';
  const rtMode: VoluumRealtimeRoutingApi =
    draft.realtimeRoutingApi ?? 'DISABLED';

  return (
    <>
      <div className="border-t border-border/60 pt-2.5">
        <FlowMetaFieldLabel
          label="Default transition in path"
          hint="Default lander→offer handoff for new paths unless a path overrides it."
        >
          <div className="space-y-1.5">
            <PillSegmentRow<VoluumOfferRedirectMode>
              aria-label="Default transition in path"
              value={redirectMode}
              options={PATH_TRANSITION_OPTIONS}
              onChange={(v) =>
                onPatch((d) => {
                  d.defaultOfferRedirectMode = v;
                })
              }
            />
            <PathTransitionFlowStrip mode={redirectMode} />
          </div>
        </FlowMetaFieldLabel>
      </div>

      <div className="border-t border-border/60 pt-2.5">
        <FlowMetaFieldLabel
          label="Routing callbacks"
          hint="Voluum routing / realtime API scope for this flow (see Voluum docs)."
        >
          <PillSegmentRow<VoluumRealtimeRoutingApi>
            aria-label="Routing callbacks"
            value={rtMode}
            options={ROUTING_CALLBACK_PILL_OPTIONS}
            onChange={(v) =>
              onPatch((d) => {
                d.realtimeRoutingApi = v;
              })
            }
          />
        </FlowMetaFieldLabel>
      </div>
    </>
  );
};

export default FlowRoutingFields;
